import React, { useState } from 'react';
import Header from './Header';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Loader from './common/Loader';
import AlertPopup from './common/AlertPopup';
import {
  getDetailsFromAppId,
  isCrifGeneratedApi,
  getCrifSimilarityWithUserInfoApi,
  getCrifAnalysisV2Api,
  getCrifDataV2Api
} from '../Access/ActionCreator';
import VerifyImage from '../assets/verified.png';
import NotVerified from '../assets/cross.png';
import noData from '../assets/nodata.png';

// Array of employee emails allowed to see and click the Regenerate button
const ALLOWED_REGEN_EMAILS = [
  'shubham.goyal@credfin.money',
  'Mayank@credfin.money',
  'siddharth@credfin.money'
];

export default function CrifNewPage({ PageHandler }) {
  const [appId, setAppId] = useState('');
  const [loader, setLoader] = useState(false);
  const [popup, setPopup] = useState({ show: false, severity: '', message: '' });

  const [analysisData, setAnalysisData] = useState(null); // stores decision and score
  const [similarityData, setSimilarityData] = useState(null); // stores matching checks
  const [crifData, setCrifData] = useState(''); // HTML report

  const showPopupMessage = (severity, message) => {
    setPopup({ show: true, severity, message });
    setTimeout(() => setPopup({ show: false, severity: '', message: '' }), 4000);
  };

  const getEmployeeId = () => {
    try {
      const SessionData = window.sessionStorage.getItem("AdminLogin");
      if (SessionData) {
        const ParsedData = JSON.parse(SessionData);
        return ParsedData.email || 'SuperUser';
      }
    } catch (e) {
      console.error(e);
    }
    return 'SuperUser';
  };

  const handleFetch = () => {
    if (!appId.trim()) {
      showPopupMessage('error', 'Please enter a valid Application ID');
      return;
    }
    setLoader(true);
    setAnalysisData(null);
    setSimilarityData(null);
    setCrifData('');

    getDetailsFromAppId(appId.trim(), (userCallback) => {
      const responseData = userCallback?.data ?? userCallback;
      if (responseData && (responseData.userId || responseData.loanId)) {
        const userId = responseData.userId || appId.trim();
        const loanId = responseData.loanId || appId.trim();
        const empId = getEmployeeId();

        let pendingCalls = 3;
        const checkLoading = () => {
          pendingCalls--;
          if (pendingCalls === 0) {
            setLoader(false);
            showPopupMessage('success', 'CRIF Data Fetched Successfully');
          }
        };

        // 1. Fetch Analysis details
        getCrifAnalysisV2Api(userId, loanId, (decisionRes) => {
          if (decisionRes) {
            setAnalysisData(decisionRes);
          }
          checkLoading();
        });

        // 2. Fetch Similarity checks
        getCrifSimilarityWithUserInfoApi(userId, loanId, (similarityRes) => {
          if (similarityRes) {
            setSimilarityData(similarityRes);
          }
          checkLoading();
        });

        // 3. Fetch HTML Report (regenerateParam = 0)
        getCrifDataV2Api(userId, loanId, 0, empId, (reportRes) => {
          if (reportRes && reportRes !== 'failure') {
            setCrifData(reportRes);
          }
          checkLoading();
        });

      } else {
        setLoader(false);
        showPopupMessage('error', userCallback?.message || 'Application ID details not found.');
      }
    });
  };

  const handleRegenerate = () => {
    if (!appId.trim()) {
      showPopupMessage('error', 'Please enter a valid Application ID');
      return;
    }
    setLoader(true);

    getDetailsFromAppId(appId.trim(), (userCallback) => {
      const responseData = userCallback?.data ?? userCallback;
      if (responseData && (responseData.userId || responseData.loanId)) {
        const userId = responseData.userId || appId.trim();
        const loanId = responseData.loanId || appId.trim();
        const empId = getEmployeeId();

        // Call regenerate (regenerateParam = 1)
        getCrifDataV2Api(userId, loanId, 1, empId, (reportRes) => {
          if (reportRes && reportRes !== 'failure') {
            setCrifData(reportRes);

            let pendingCalls = 2;
            const checkLoading = () => {
              pendingCalls--;
              if (pendingCalls === 0) {
                setLoader(false);
                showPopupMessage('success', 'CRIF Data Regenerated Successfully');
              }
            };

            // Refresh Analysis details
            getCrifAnalysisV2Api(userId, loanId, (decisionRes) => {
              if (decisionRes) {
                setAnalysisData(decisionRes);
              }
              checkLoading();
            });

            // Refresh Similarity checks
            getCrifSimilarityWithUserInfoApi(userId, loanId, (similarityRes) => {
              if (similarityRes) {
                setSimilarityData(similarityRes);
              }
              checkLoading();
            });

          } else {
            setLoader(false);
            showPopupMessage('error', 'Failed to regenerate CRIF report.');
          }
        });
      } else {
        setLoader(false);
        showPopupMessage('error', userCallback?.message || 'Application ID details not found.');
      }
    });
  };

  // Helper function to color code decision card
  const getDecisionStyles = (decision) => {
    if (!decision) return { color: '#666', border: '1px solid #ddd', bg: '#f9f9f9' };
    const dec = decision.toLowerCase();
    if (dec.includes('reject')) {
      return { color: '#e53935', border: '1.5px solid #ffcdd2', bg: 'rgba(255, 235, 235, 0.9)' };
    }
    if (dec.includes('approve') || dec.includes('success')) {
      return { color: '#2e7d32', border: '1.5px solid #c8e6c9', bg: 'rgba(232, 245, 233, 0.9)' };
    }
    return { color: '#ef6c00', border: '1.5px solid #ffe0b2', bg: 'rgba(255, 243, 224, 0.9)' };
  };

  // Extract variables with fallback support for all shapes
  const score = analysisData?.finBoxDecision?.score ?? analysisData?.score ?? analysisData?.data?.score ?? 'N/A';
  const finalDecision = analysisData?.finBoxDecision?.finalDecision ?? analysisData?.finalDecision ?? analysisData?.data?.finalDecision ?? 'N/A';

  const dualPan = similarityData?.finBoxDecision?.dualPan ?? similarityData?.dualPan ?? similarityData?.data?.dualPan;
  const mobileMatched = similarityData?.finBoxDecision?.mobileMatched ?? similarityData?.mobileMatched ?? similarityData?.data?.mobileMatched;
  const pincodeMatched = similarityData?.finBoxDecision?.currentAddressPincodeMatched ?? similarityData?.currentAddressPincodeMatched ?? similarityData?.pincodeMatched ?? similarityData?.data?.currentAddressPincodeMatched ?? similarityData?.data?.pincodeMatched;

  const decisionStyle = getDecisionStyles(finalDecision);
  const empEmail = getEmployeeId();
  const isRegenAllowed = ALLOWED_REGEN_EMAILS.includes(empEmail);

  return (
    <>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <div className="Dashboard" style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
        <Header PageHandler={PageHandler} />

        {loader && <Loader />}
        {popup.show && <AlertPopup severity={popup.severity} message={popup.message} />}

        <div className="Main-content" style={{ padding: '30px' }}>
          <div className="container-fluid">
            <Box
              sx={{
                background: 'rgba(255, 255, 255, 0.85)',
                backdropFilter: 'blur(10px)',
                borderRadius: '16px',
                padding: '30px',
                boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.18)',
                mb: 4
              }}
            >
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a237e', mb: 3, textAlign: 'center' }}>
                CRIF Analysis Hub
              </Typography>

              <Grid container spacing={2} justifyContent="center" alignItems="center" sx={{ mb: 4 }}>
                <Grid item xs={12} sm={isRegenAllowed ? 6 : 8} md={isRegenAllowed ? 5 : 7}>
                  <TextField
                    fullWidth
                    label="Enter Application ID"
                    variant="outlined"
                    value={appId}
                    onChange={(e) => setAppId(e.target.value)}
                    sx={{
                      backgroundColor: '#fff',
                      borderRadius: '8px',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderRadius: '8px' },
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={isRegenAllowed ? 6 : 12} sm={isRegenAllowed ? 3 : 4} md={isRegenAllowed ? 2 : 3}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleFetch}
                    sx={{
                      height: '56px',
                      borderRadius: '8px',
                      background: 'linear-gradient(45deg, #3f51b5 30%, #2196f3 90%)',
                      color: 'white',
                      fontWeight: 'bold',
                      boxShadow: '0 3px 5px 2px rgba(33, 150, 243, .3)',
                      transition: 'transform 0.2s',
                      '&:hover': {
                        transform: 'scale(1.03)',
                        background: 'linear-gradient(45deg, #3f51b5 30%, #2196f3 90%)',
                      }
                    }}
                  >
                    Fetch
                  </Button>
                </Grid>
                {isRegenAllowed && (
                  <Grid item xs={6} sm={3} md={2}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleRegenerate}
                      sx={{
                        height: '56px',
                        borderRadius: '8px',
                        background: 'linear-gradient(45deg, #00b0ff 30%, #00e5ff 90%)',
                        color: 'white',
                        fontWeight: 'bold',
                        boxShadow: '0 3px 5px 2px rgba(0, 229, 255, .3)',
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: 'scale(1.03)',
                          background: 'linear-gradient(45deg, #00b0ff 30%, #00e5ff 90%)',
                        }
                      }}
                    >
                      Regenerate
                    </Button>
                  </Grid>
                )}
              </Grid>

              {(analysisData || similarityData) && (
                <Box sx={{ mt: 4, animation: 'fadeIn 0.5s ease-in-out' }}>
                  <Grid container spacing={3} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                      <Card
                        sx={{
                          border: decisionStyle.border,
                          background: decisionStyle.bg,
                          borderRadius: '12px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                        }}
                      >
                        <CardContent sx={{ textAlign: 'center', py: 3 }}>
                          <Typography variant="subtitle1" sx={{ color: '#555', fontWeight: 500, mb: 1 }}>
                            Automated Decision
                          </Typography>
                          <Typography variant="h4" sx={{ color: decisionStyle.color, fontWeight: 700 }}>
                            {finalDecision}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Card
                        sx={{
                          border: '1.5px solid #bbdefb',
                          background: 'rgba(227, 242, 253, 0.9)',
                          borderRadius: '12px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                        }}
                      >
                        <CardContent sx={{ textAlign: 'center', py: 3 }}>
                          <Typography variant="subtitle1" sx={{ color: '#555', fontWeight: 500, mb: 1 }}>
                            CIBIL Score
                          </Typography>
                          <Typography variant="h4" sx={{ color: '#0d47a1', fontWeight: 700 }}>
                            {score}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>

                  <Box
                    sx={{
                      background: 'rgba(242, 242, 250, 0.9)',
                      border: '1px solid #D3D9E9',
                      borderRadius: '12px',
                      padding: '20px',
                      display: 'flex',
                      justifyContent: 'space-around',
                      flexWrap: 'wrap',
                      gap: 2,
                      mb: 4,
                      boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)'
                    }}
                  >
                    <Box sx={{ textAlign: 'center', minWidth: '120px' }}>
                      <Typography variant="subtitle2" sx={{ color: '#4a4a4a', fontWeight: 600, mb: 1 }}>
                        Dual Pan
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40px' }}>
                        <img
                          src={dualPan === true ? VerifyImage : NotVerified}
                          alt="status"
                          width="24px"
                        />
                      </Box>
                    </Box>

                    <Box sx={{ textAlign: 'center', minWidth: '120px' }}>
                      <Typography variant="subtitle2" sx={{ color: '#4a4a4a', fontWeight: 600, mb: 1 }}>
                        Mobile Matched
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40px' }}>
                        <img
                          src={mobileMatched === true ? VerifyImage : NotVerified}
                          alt="status"
                          width="24px"
                        />
                      </Box>
                    </Box>

                    <Box sx={{ textAlign: 'center', minWidth: '120px' }}>
                      <Typography variant="subtitle2" sx={{ color: '#4a4a4a', fontWeight: 600, mb: 1 }}>
                        Current Address Pincode Matched
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40px' }}>
                        <img
                          src={pincodeMatched === true ? VerifyImage : NotVerified}
                          alt="status"
                          width="24px"
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              )}

              {crifData && (
                <Box
                  sx={{
                    mt: 3,
                    border: '1px solid #D3D9E9',
                    borderRadius: '12px',
                    padding: '20px',
                    backgroundColor: '#fff',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    animation: 'fadeIn 0.4s ease-in-out'
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a237e', mb: 2, borderBottom: '2px solid #e0e0e0', pb: 1 }}>
                    CRIF Detailed Report
                  </Typography>
                  <Box
                    id="html-renderer"
                    sx={{
                      maxHeight: '600px',
                      overflowY: 'auto',
                      padding: '10px',
                      border: '1px solid #eee',
                      borderRadius: '8px',
                      '& table': {
                        width: '100% !important',
                        borderCollapse: 'collapse',
                        margin: '10px 0'
                      },
                      '& th, & td': {
                        border: '1px solid #ddd',
                        padding: '8px',
                        textAlign: 'left'
                      },
                      '& th': {
                        backgroundColor: '#f5f5f5'
                      }
                    }}
                    dangerouslySetInnerHTML={{ __html: crifData }}
                  />
                </Box>
              )}

              {!analysisData && !similarityData && !loader && (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                  <img src={noData} alt="No Data" style={{ width: '150px', opacity: 0.6, marginBottom: '15px' }} />
                  <Typography variant="body1" sx={{ color: '#777', fontWeight: 500 }}>
                    Enter an Application ID to fetch analysis results.
                  </Typography>
                </Box>
              )}
            </Box>
          </div>
        </div>
      </div>
    </>
  );
}
